/* eslint-disable */
// @ts-nocheck

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { AreaQueryParams, useAreaQuery } from '@/hooks/areas/useAreasQuery'
import { Area } from '@/interfaces/area'
import { storage } from '@/lib/utils'
import { useAppSelector } from '@/store/hooks'

import { AreaFilters, useAreaFilters } from './areaFilters/hooks/useAreaFilters'

interface AreaData {
  items: Area[]
  totalItems: number
  currentPage: number
  totalPages: number
}

// Define the context type
interface TableContextType {
  currentPage: number
  setCurrentPage: (page: number) => void
  isFetching: boolean
  isError: boolean
  pageSize: number
  setPageSize: (size: number) => void
  areaData: AreaData | null
  isLoading: boolean
  refetch: () => void
  // Add direct access to pagination methods for convenience
  goToNextPage: () => void
  goToPreviousPage: () => void
  // Skeleton data for loading state
  skeletonRowCount: number
  // Additional helpful properties
  hasNextPage: boolean
  hasPreviousPage: boolean
  // Filter management
  resetToFirstPage: () => void
}

// Create the context with a default value
const TableContext = createContext<TableContextType | undefined>(undefined)

// Default values
const INITIAL_PAGE = 1
const INITIAL_PAGE_SIZE = 20

// Storage keys
const STORAGE_KEYS = {
  CURRENT_PAGE: 'billing-table-current-page',
  PAGE_SIZE: 'billing-table-page-size',
} as const

// Helper to create a filter key for comparison
const createFilterKey = (filters: AreaFilters): string => {
  return JSON.stringify({
    search: filters.search || '',
    status: [...filters.isActive].sort(),
    type: [...filters.type].sort(),
  })
}

// Provider component that will wrap the table components
export const TableContextProvider = ({ children }: { children: ReactNode }) => {
  // Get filters from the billing filters hook (now with built-in localStorage persistence)
  const { filters } = useAreaFilters()
  const { business } = useAppSelector((state) => state.init)

  // Track previous filter state to detect changes
  const previousFiltersRef = useRef<string>('')
  const isInitialMount = useRef(true)

  // Initialize state from localStorage
  const [currentPage, setCurrentPageState] = useState(() =>
    storage.get(STORAGE_KEYS.CURRENT_PAGE, INITIAL_PAGE)
  )

  const [pageSize, setPageSizeState] = useState(() =>
    storage.get(STORAGE_KEYS.PAGE_SIZE, INITIAL_PAGE_SIZE)
  )

  // Check if filters have changed (excluding pagination-related changes)
  const currentFilterKey = createFilterKey(filters)
  const filtersHaveChanged =
    previousFiltersRef.current !== currentFilterKey && !isInitialMount.current

  // Update previous filters reference
  useEffect(() => {
    previousFiltersRef.current = currentFilterKey
    isInitialMount.current = false
  }, [currentFilterKey])

  // Reset to page 1 when filters change (but not on initial mount)
  useEffect(() => {
    if (filtersHaveChanged && currentPage !== INITIAL_PAGE) {
      setCurrentPageState(INITIAL_PAGE)
      storage.set(STORAGE_KEYS.CURRENT_PAGE, INITIAL_PAGE)
    }
  }, [filtersHaveChanged, currentPage])

  // Build query parameters from current state and filters
  const queryParams: AreaQueryParams = {
    page: currentPage,
    per_page: pageSize,
    search: filters.search || null,
    ...(filters.isActive.includes('all')
      ? {}
      : {
          isActive: filters.isActive.join(','),
        }),
    ...(filters.type.includes('all')
      ? {}
      : {
          type: filters.type.join(','),
        }),
  }

  // Use the invoice query hook with the current parameters
  const {
    data: areaData,
    isLoading,
    isFetching,
    isError,
    refetch,
    pagination,
  } = useAreaQuery({
    params: queryParams,
    options: {
      staleTime: 5 * 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: true,
    },
    businessId: business?.id ?? 0,
  })

  // Handle page changes with localStorage persistence
  const setCurrentPage = useCallback((page: number) => {
    setCurrentPageState(page)
    storage.set(STORAGE_KEYS.CURRENT_PAGE, page)
  }, [])

  // Handle page size changes with localStorage persistence
  const setPageSize = useCallback(
    (size: number) => {
      setPageSizeState(size)
      storage.set(STORAGE_KEYS.PAGE_SIZE, size)

      // Reset to first page when changing page size (if not already on page 1)
      if (currentPage !== INITIAL_PAGE) {
        setCurrentPage(INITIAL_PAGE)
      }
    },
    [currentPage, setCurrentPage]
  )

  // Navigation helpers
  const goToNextPage = useCallback(() => {
    if (pagination.hasNextPage) {
      setCurrentPage(currentPage + 1)
    }
  }, [currentPage, setCurrentPage, pagination.hasNextPage])

  const goToPreviousPage = useCallback(() => {
    if (pagination.hasPreviousPage) {
      setCurrentPage(currentPage - 1)
    }
  }, [currentPage, setCurrentPage, pagination.hasPreviousPage])

  // Helper to reset to first page (useful for external components)
  const resetToFirstPage = useCallback(() => {
    if (currentPage !== INITIAL_PAGE) {
      setCurrentPage(INITIAL_PAGE)
    }
  }, [currentPage, setCurrentPage])

  // Sync with API response if there's a mismatch (fallback safety)
  useEffect(() => {
    if (areaData?.currentPage && areaData.currentPage !== currentPage) {
      setCurrentPageState(areaData.currentPage)
      storage.set(STORAGE_KEYS.CURRENT_PAGE, areaData.currentPage)
    }
  }, [areaData?.currentPage, currentPage])

  // Create the context value object
  const contextValue: TableContextType = {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    areaData: areaData || null,
    isLoading,
    isFetching,
    isError,
    refetch,
    goToNextPage,
    goToPreviousPage,
    skeletonRowCount: pageSize,
    hasNextPage: pagination.hasNextPage,
    hasPreviousPage: pagination.hasPreviousPage,
    resetToFirstPage,
  }

  return (
    <TableContext.Provider value={contextValue}>
      {children}
    </TableContext.Provider>
  )
}

// Custom hook to use the table context
export const useTableContext = () => {
  const context = useContext(TableContext)
  if (context === undefined) {
    throw new Error(
      'useTableContext must be used within a TableContextProvider'
    )
  }
  return context
}

// Export storage utilities for external use if needed
export const tableStorage = {
  clearAll: () => {
    Object.values(STORAGE_KEYS).forEach((key) => storage.remove(key))
  },
  getSavedPage: () => storage.get(STORAGE_KEYS.CURRENT_PAGE, INITIAL_PAGE),
  getSavedPageSize: () =>
    storage.get(STORAGE_KEYS.PAGE_SIZE, INITIAL_PAGE_SIZE),
}
